import React from 'react'
import styled from '@emotion/styled'
import { withTranslation } from '~/i18n'
import SearchOverlay from '../components/SearchOverlay'
import { WidePageLayout } from '~/components/PageLayout'
import { H1, color, space, device, fontWeight, Button } from '@ticketswap/solar'
import ResponseStatusCode from '../components/StatusCode'
import Menubar from '../components/Menubar'
import { MenubarContainer } from '../components/Menubar'
import { NextSeo } from 'next-seo'
import SiteFooter from '../components/SiteFooter'
import { useViewer } from '~/components/ViewerContext'
import { createMarkup, getLinkProps, track } from '~/utils'
import { AUTHENTICATION_VIEWED } from '~/lib/tracking-events'
import AuthDialog from '~/components/AuthDialog'
import { execute } from '~/utils/nativeBridge'
import Link from 'next/link'
import Router from 'next/router'

const menuItems = [
  { title: 'footer.primary.home', ...getLinkProps('HOME') },
  {
    title: 'footer.primary.howItWorks',
    ...getLinkProps('HOW_DOES_IT_WORK'),
  },
  {
    title: 'footer.primary.aboutUs',
    ...getLinkProps('CONTENT', { slug: 'about-us' }),
  },
  { title: 'footer.primary.contact', ...getLinkProps('HELP') },
  { title: 'menubar.sellTickets', ...getLinkProps('SELL') },
]

const Content = styled.div`
  margin: ${space[96]} 0;

  @media ${device.tablet} {
    margin: ${space[256]} 0;
    margin: 25vh 0;
    width: 35%;
  }
`

const IllustrationWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  margin-bottom: ${space[32]};

  @media ${device.tablet} {
    justify-content: flex-end;
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    margin: 0;
    width: 65%;
    z-index: -1;
  }
`

const Illustration = styled.span`
  color: ${color.stardust};
  font-weight: ${fontWeight.bold};
  font-size: 12rem;
  line-height: 0.75;
  font-variant-numeric: tabular-nums;

  @media ${device.tablet} {
    font-size: 20rem;
    transform: translateY(1rem);
  }
`

const StyledH1 = styled(H1)`
  transform: translateX(-1px);
  margin-bottom: ${space[16]};

  @media ${device.tablet} {
    transform: translateX(-2px);
  }
`

const Paragraph = styled.p`
  color: ${color.spaceMedium};
  margin-bottom: ${space[24]};
  max-width: ${space[512]};
`

const List = styled.ul`
  max-width: ${space[512]};
  display: grid;
  grid-template-columns: repeat(2, 1fr);
`

const StyledLink = styled.a`
  color: ${color.earth};
`

function Error({ t, statusCode }) {
  const { viewer, deviceInfo } = useViewer()

  const statusCodes = {
    400: 'Bad Request',
    403: Boolean(viewer)
      ? t('error.noAccess.loggedIn')
      : t('error.noAccess.loggedOut'),
    404: t('error.pageNotFound'),
    500: 'Internal Server Error',
    501: 'Not Implemented',
  }
  const noAccess = statusCode === 403
  const title = noAccess ? t('error.noAccess.title') : `${t('error.oops')}…`
  const description = statusCodes[statusCode] || t('error.clientError')

  function openLoginDialog(show) {
    if (deviceInfo.isApp) {
      return execute('showMultiLogin', {
        isFromWebView: true,
      })
    }
    show()
  }

  return (
    <>
      <NextSeo
        title={`${t('common:site.name')}: ${t('common:site.title')}`}
        openGraph={{
          title: `${t('common:site.name')}: ${t('common:site.title')}`,
          site_name: t('common:site.name'),
        }}
      />
      <MenubarContainer>
        <Menubar />
      </MenubarContainer>
      <ResponseStatusCode statusCode={statusCode} />
      <WidePageLayout>
        <Content>
          <IllustrationWrapper>
            <Illustration>{statusCode}</Illustration>
          </IllustrationWrapper>
          <StyledH1>{title}</StyledH1>
          <Paragraph>
            <span
              dangerouslySetInnerHTML={createMarkup(description, {
                renderInline: true,
              })}
            />
            {!noAccess && ` ${t('error.helpfulLinks')}:`}
          </Paragraph>
          {!noAccess && (
            <List>
              {menuItems.map(({ href, as, title }) => (
                <li key={href}>
                  <Link as={as} href={href} passHref>
                    <StyledLink>{t(title)}</StyledLink>
                  </Link>
                </li>
              ))}
              <li>
                <SearchOverlay>
                  {({ getToggleProps }) => (
                    <StyledLink as="button" {...getToggleProps()}>
                      {t('search.label')}
                    </StyledLink>
                  )}
                </SearchOverlay>
              </li>
            </List>
          )}
          {noAccess && !viewer && (
            <AuthDialog
              // Remove redirect logic after sellflow pages are able to update
              // the viewer using the hook. This is used for refetching the
              // viewer in the getInitialProps of the page.
              onComplete={() => Router.replace(window.location.pathname)}
              onToggle={on => on && track(AUTHENTICATION_VIEWED)}
            >
              {({ show }) => (
                <Button onClick={() => openLoginDialog(show)}>
                  {t('common:menubar.login')}
                </Button>
              )}
            </AuthDialog>
          )}
        </Content>
      </WidePageLayout>
      <SiteFooter />
    </>
  )
}

Error.getInitialProps = async function ({ res, err }) {
  const statusCode = res ? res.statusCode : err ? err.statusCode : null

  return { statusCode, namespacesRequired: ['common'] }
}

export default withTranslation(['common'])(Error)
